import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  FaEdit, 
  FaTrashAlt, 
  FaCopy, 
  FaSearch, 
  FaChevronDown,
  FaEye,
  FaTag,
  FaUpload
} from 'react-icons/fa';
import VendorSidebar from './VendorSidebar';
import Navbar from '../components/navbar';
import './AllProducts.css';
import noimg from '../icons/noimg.png';

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortBy, setSortBy] = useState('lastEdit');
  const [filterStatus, setFilterStatus] = useState('all');
  const [vendorData, setVendorData] = useState({ selectType: '' });

  useEffect(() => {
    fetchProducts();
    fetchVendorData();
  }, []);

  const fetchVendorData = async () => {
    try {
      const vendortoken = localStorage.getItem('vendortoken');
      if (vendortoken) {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/vendorData`, { vendortoken });
        if (response.data.status === 'ok') {
          setVendorData(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
    }
  };

  const fetchProducts = async () => {
    const vendorId = localStorage.getItem('vendorId');
    if (!vendorId) {
      alert('Vendor ID not found in local storage');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/getVendorProduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ vendorId })
      });

      const data = await response.json();
      if (data.status === 'ok') {
        setProducts(data.data);
      } else {
        setMessage('Error fetching products: ' + data.message);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setMessage('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (productId) => {
    window.location.href = `/Vendor/UpdateProduct/${productId}`;
  };

  const handleDelete = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/deleteProductVendor`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ productId })
        });

        const data = await response.json();
        if (data.status === 'ok') {
          setProducts(products.filter(product => product._id !== productId));
          setMessage('Product deleted successfully');
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error deleting product: ' + data.message);
        }
      } catch (error) {
        console.error('Delete error:', error);
        setMessage('Error deleting product');
      }
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(product => product._id));
    }
  };

  const handleBulkAction = (action) => {
    if (selectedProducts.length === 0) {
      alert('Please select products first');
      return;
    }
    
    if (action === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} product(s)?`)) {
        // Handle bulk delete
        console.log('Bulk delete:', selectedProducts);
      }
    }
  };

  // Filter and search products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && product.active) || 
      (filterStatus === 'inactive' && !product.active);
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  const getStatusBadge = (active) => {
    return active ? (
      <span className="status-badge active">Active</span>
    ) : (
      <span className="status-badge inactive">Inactive</span>
    );
  };

  const getQualityLevel = (product) => {
    // Calculate quality based on product completeness
    let score = 0;
    if (product.name && product.name.length > 10) score += 25;
    if (product.description && product.description.length > 50) score += 25;
    if (product.image && product.image.length > 0) score += 25;
    if (product.sellingPrice) score += 25;
    
    if (score >= 80) return { level: 'High', class: 'high' };
    if (score >= 60) return { level: 'Medium', class: 'medium' };
    return { level: 'Low', class: 'low' };
  };

  return (
    <>
      <Navbar />
      <div className="products-management-container">
        <VendorSidebar />
        
        <div className="content-area">
          {/* Header */}
          <div className="page-header">
            <div className="header-content">
              <h1 className="page-title">Manage Products</h1>
              <div className="header-actions">
                <button className="help-btn">
                  <i className="fas fa-question-circle"></i> Help Center
                </button>
              
                <button className="feedback-btn">
                  <i className="fas fa-comment"></i> Your Feedback
                </button>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="products-section">
            {/* Tab Navigation */}
            <div className="products-tabs">
              <div className="tab active">
                All Products {filteredProducts.length}
              </div>
            </div>

            {/* Action Bar */}
            <div className="action-bar">
              <div className="bulk-actions">
                <button 
                  className="action-btn primary"
                  onClick={() => window.location.href = '/Vendor/AddProductVendor'}
                >
                  Submit
                </button>
                <button 
                  className="action-btn"
                  onClick={() => handleBulkAction('unpost')}
                >
                  Unpost
                </button>
                <button 
                  className="action-btn"
                  onClick={() => handleBulkAction('delete')}
                >
                  Delete
                </button>
                <div className="dropdown-action">
                  <button className="action-btn-vendorProduct dropdown">
                    Tag <FaChevronDown />
                  </button>
                </div>
                <div className="dropdown-action">
                  <button className="action-btn-vendorProduct dropdown">
                    More Actions <FaChevronDown />
                  </button>
                </div>
              </div>

              <div className="search-actions">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Please enter product name"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <button className="search-btn">
                    <FaSearch /> Search
                  </button>
                </div>
                <button className="advanced-search-btn">
                  Advanced Search <FaChevronDown />
                </button>
              </div>
            </div>

            {/* Products Table */}
            <div className="products-table-container">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading products...</p>
                </div>
              ) : (
                <table className="products-table">
                  <thead>
                    <tr>
                      <th className="checkbox-col">
                        <input 
                          type="checkbox"
                          checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="product-col">Product</th>
                      <th className="category-col">Category</th>
                      <th className="quality-col">Product Quality Level</th>
                      <th className="owner-col">Owner</th>
                      <th className="status-col">Status</th>
                      <th className="date-col">
                        Last Edit <FaChevronDown />
                      </th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.length > 0 ? (
                      paginatedProducts.map((product) => {
                        const quality = getQualityLevel(product);
                        return (
                          <tr key={product._id} className="product-row">
                            <td className="checkbox-col">
                              <input 
                                type="checkbox"
                                checked={selectedProducts.includes(product._id)}
                                onChange={() => handleSelectProduct(product._id)}
                              />
                            </td>
                            <td className="product-vendorcol">
                              <div className="product-vendorinfo">
                                <div className="product-vendorimage">
                                  {product.image && product.image.length > 0 ? (
                                    <img
                                      src={`${process.env.REACT_APP_API_URL}/${product.image[0].replace('\\', '/')}`}
                                      alt={product.name}
                                      onError={(e) => {
                                        e.target.src = noimg;
                                      }}
                                    />
                                  ) : (
                                    <img src={noimg} alt="No image" />
                                  )}
                                </div>
                                <div className="product-details">
                                  <h5 className="product-name">{product.name}</h5>
                                  <p className="product-model">Model NO.: {product.slug}</p>
                                </div>
                              </div>
                            </td>
                            <td className="category-col">
                              {product.category?.name || 'Unknown Category'}
                            </td>
                            <td className="quality-col">
                              <div className={`quality-indicator ${quality.class}`}>
                                <span className="quality-dot"></span>
                                {quality.level}
                              </div>
                            </td>
                            <td className="owner-col">
                              {vendorData.businessName || 'Vendor'}
                            </td>
                            <td className="status-col">
                              {getStatusBadge(product.active)}
                            </td>
                            <td className="date-col">
                              {new Date(product.updatedAt).toLocaleDateString()}
                            </td>
                            <td className="actions-col">
                              <div className="action-buttons">
                                <button 
                                  className="action-icon edit"
                                  onClick={() => handleUpdate(product._id)}
                                  title="Edit"
                                >
                                  Edit
                                </button>
                                <button 
                                  className="action-icon copy"
                                  title="Copy"
                                >
                                  <FaCopy />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="no-products">
                          <div className="empty-state">
                            <i className="fas fa-box-open"></i>
                            <h4>No Products Found</h4>
                            <p>Start by adding your first product</p>
                            <Link to="/Vendor/AddProductVendor" className="add-product-btn">
                              Add New Product
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {!loading && filteredProducts.length > 0 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Total {filteredProducts.length} items
                </div>
                <div className="pagination">
                  <button 
                    className="page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    ‹
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button 
                    className="page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    ›
                  </button>
                </div>
                <div className="items-per-page">
                  <select 
                    value={itemsPerPage} 
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                  </select>
                </div>
              </div>
            )}

            {message && (
              <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AllProducts;